/** Mirrors the backend's single error envelope: `{ error: { code, message, details } }`. */
export type ApiErrorBody = {
  error: { code: string; message: string; details?: unknown };
};

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** A 501 is a route the backend has stubbed, not a failure worth alarming about. */
  get isNotImplemented(): boolean {
    return this.status === 501;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function errorMessage(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong';
}

/**
 * The request never reached the server: no network, DNS gone, the box is down,
 * or it took so long that waiting was worse than failing.
 *
 * Modelled as `status: 0` — deliberately outside every HTTP range — so the one
 * question callers actually need to ask is cheap: did the server say no, or did
 * nobody answer? A counter can queue a bill for the second and must not for the
 * first.
 */
export class NetworkError extends ApiError {
  constructor(message = 'Could not reach the server') {
    super(0, 'NETWORK_UNREACHABLE', message);
    this.name = 'NetworkError';
  }
}

export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}
